import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'

import { apiClient, ApiError } from '../services/api'
import LoadingSpinner from './LoadingSpinner'
import { formatDateTime, formatEur, formatPeriod } from '../utils/deductibleFormat'

/**
 * Copayment history of one trip (billing, corrections, whole-invoice credits,
 * undone corrections) with who/when for every step. Opened from the
 * deductible invoices page (by deductible line) and from the billing
 * materials inspection table (by inspection entry).
 */

export interface HistoryEvent {
  at: string | null
  type: 'billed' | 'generated' | 'correction' | 'invoice_credit' | 'correction_undone'
  by?: string | null
  period?: string
  invoice_number?: number | null
  line_number?: number | null
  value?: string
  row_kind?: string
  invoice_credit_role?: string | null
  correction_type?: string
  from?: string | null
  to?: string | null
  reason?: string | null
  materialized?: boolean
  role?: string | null
  credited_invoice_number?: number | null
  credit_invoice_number?: number | null
  replacement_invoice_number?: number | null
  status?: string
}

export type HistorySource = { lineId: string } | { inspectionEntryId: string }

/** A timeline entry: one raw event, or several events collapsed into one. */
interface HistoryGroup {
  at: string | null
  by?: string | null
  title: string
  lines: string[]
  reason?: string | null
}

/** Row value behind a correction record: a credit reverses the original
 *  value (row = -from), a correction charges the new value (row = to). */
function correctionRowValue(ev: HistoryEvent): number {
  return ev.correction_type === 'credit' ? -Number(ev.from ?? 0) : Number(ev.to ?? 0)
}

function historyPath(source: HistorySource): string {
  return 'lineId' in source
    ? `/billing/deductibles/lines/${source.lineId}/history`
    : `/billing/deductibles/inspection-entries/${source.inspectionEntryId}/history`
}

export default function DeductibleHistoryModal({
  source,
  subtitle,
  onClose,
}: {
  source: HistorySource
  subtitle?: string
  onClose: () => void
}) {
  const { t } = useTranslation('billing')
  const path = historyPath(source)
  const { data, isLoading, error } = useQuery<HistoryEvent[]>({
    queryKey: ['deductibleLineHistory', path],
    queryFn: () => apiClient.get(path),
  })

  const rowKind = (kind?: string) => t(`deductibleInvoices.history.rowKind.${kind ?? 'original'}`)

  // Collapse: all rows of one invoice -> one entry; all correction records of
  // one whole-invoice credit -> one entry (credit rows + replacement rows).
  const groups: HistoryGroup[] = []
  type BilledGroup = HistoryGroup & {
    rows: { line: number; text: string }[]
    invoice: number | null
    period: string
  }
  const billedByInvoice = new Map<string, BilledGroup>()
  type CreditGroup = HistoryGroup & {
    h: number[]
    k: number[]
    creditInvoice: number | null
    replacementInvoice: number | null
  }
  const creditByIc = new Map<string, CreditGroup>()
  for (const ev of data ?? []) {
    switch (ev.type) {
      case 'billed':
      case 'generated': {
        const key = `${ev.type}|${ev.period}|${ev.invoice_number ?? ''}`
        let g = billedByInvoice.get(key)
        if (!g) {
          g = {
            at: ev.at,
            by: ev.by,
            title:
              ev.type === 'billed'
                ? t('deductibleInvoices.history.billedGroup', {
                    invoice: ev.invoice_number,
                    period: ev.period ? formatPeriod(ev.period) : '',
                  })
                : t('deductibleInvoices.history.generatedGroup', {
                    period: ev.period ? formatPeriod(ev.period) : '',
                  }),
            lines: [],
            rows: [],
            invoice: ev.invoice_number ?? null,
            period: ev.period ?? '',
          }
          billedByInvoice.set(key, g)
          groups.push(g)
        }
        g.rows.push({
          line: ev.line_number ?? 0,
          text: t('deductibleInvoices.history.rowShort', {
            line: ev.line_number ?? '–',
            value: formatEur(ev.value),
            kind: rowKind(ev.row_kind),
          }),
        })
        g.rows.sort((a, b) => a.line - b.line)
        g.lines = [g.rows.map((r) => r.text).join(', ')]
        break
      }
      case 'invoice_credit': {
        const key = `${ev.credited_invoice_number}|${ev.at}`
        let g = creditByIc.get(key)
        if (!g) {
          g = {
            at: ev.at,
            by: ev.by,
            reason: ev.reason,
            title: t('deductibleInvoices.history.invoiceCreditGroup', {
              credited: ev.credited_invoice_number,
              status: t(`deductibleInvoices.creditStatus.${ev.status ?? 'pending'}`),
            }),
            lines: [],
            h: [],
            k: [],
            creditInvoice: ev.credit_invoice_number ?? null,
            replacementInvoice: ev.replacement_invoice_number ?? null,
          }
          creditByIc.set(key, g)
          groups.push(g)
        }
        const bucket = ev.role === 'credit' ? g.h : g.k
        bucket.push(correctionRowValue(ev))
        const pendingLabel = t('deductibleInvoices.history.pendingInvoice')
        g.lines = [
          t('deductibleInvoices.history.creditRows', {
            invoice: ev.credit_invoice_number ?? pendingLabel,
            values: g.h.map(formatEur).join(', '),
          }),
          ...(g.k.length
            ? [
                t('deductibleInvoices.history.replacementRows', {
                  invoice: ev.replacement_invoice_number ?? pendingLabel,
                  values: g.k.map(formatEur).join(', '),
                }),
              ]
            : []),
        ]
        break
      }
      case 'correction':
        groups.push({
          at: ev.at,
          by: ev.by,
          reason: ev.reason,
          title: t('deductibleInvoices.history.correction', {
            type: t(`deductibleInvoices.history.correctionType.${ev.correction_type ?? 'correct'}`),
            from: formatEur(ev.from),
            to: formatEur(ev.to),
            state:
              ev.correction_type === 'direct'
                ? t('deductibleInvoices.history.directApplied')
                : ev.materialized
                  ? t('deductibleInvoices.history.materialized')
                  : t('deductibleInvoices.history.pending'),
          }),
          lines: [],
        })
        break
      case 'correction_undone':
        groups.push({
          at: ev.at,
          by: ev.by,
          title: t('deductibleInvoices.history.undone', { from: formatEur(ev.from) }),
          lines: [],
        })
        break
      default:
        groups.push({ at: ev.at, by: ev.by, title: ev.type, lines: [] })
    }
  }

  // The credit entry already lists the rows of the credit and replacement
  // invoices; fold their billing entries into it instead of repeating rows.
  const billedList = Array.from(billedByInvoice.values())
  const dropped = new Set<HistoryGroup>()
  for (const g of creditByIc.values()) {
    const billed = billedList.filter(
      (b) =>
        b.invoice !== null &&
        (b.invoice === g.creditInvoice || b.invoice === g.replacementInvoice),
    )
    if (billed.length === 0) continue
    const first = billed[0]
    g.lines.push(
      t('deductibleInvoices.history.creditBilled', {
        period: first.period ? formatPeriod(first.period) : '',
        at: formatDateTime(first.at),
        by: first.by ?? '',
      }),
    )
    billed.forEach((b) => dropped.add(b))
  }
  const timeline = groups.filter((g) => !dropped.has(g))

  const errorText =
    error instanceof ApiError
      ? error.code === 'NOT_FOUND'
        ? t('deductibleInvoices.history.notLinked')
        : error.message
      : error instanceof Error
        ? error.message
        : null

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('deductibleInvoices.history.title')}
            </h3>
            {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        {isLoading && <LoadingSpinner />}
        {errorText && <div className="text-sm text-red-600">{errorText}</div>}
        {data && (
          <ol className="relative border-l border-gray-200 ml-2">
            {timeline.map((g, i) => (
              <li key={i} className="mb-4 ml-4">
                <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full bg-primary-500 border border-white" />
                <div className="text-xs text-gray-500">
                  {formatDateTime(g.at)}
                  {g.by && ` · ${g.by}`}
                </div>
                <div className="text-sm text-gray-900">{g.title}</div>
                {g.lines.map((ln, j) => (
                  <div key={j} className="text-xs text-gray-600">
                    {ln}
                  </div>
                ))}
                {g.reason && <div className="text-xs text-gray-500 italic">{g.reason}</div>}
              </li>
            ))}
            {timeline.length === 0 && (
              <li className="ml-4 text-sm text-gray-500">{t('deductibleInvoices.history.empty')}</li>
            )}
          </ol>
        )}
      </div>
    </div>
  )
}
